import {
  BASE_RECORD_SURVIVORSHIP_TYPES,
  SOURCE_SURVIVORSHIP_TYPES,
  SURVIVORSHIP_TYPES,
  SurvivorshipTypes,
} from "@hightouch/lib/idr/golden-record/constants";
import { IdrSimpleProbabilisticConfigSchema } from "@hightouch/lib/idr/create-probabilistic";
import { IdrCreateGraphScheduleSchema } from "@hightouch/lib/idr/create-schedule";
import { z } from "zod/v4";

export {
  IdrCreateGraphScheduleSchema,
  type IdrCreateGraphSchedule,
} from "@hightouch/lib/idr/create-schedule";
export {
  IdrSimpleProbabilisticConfigSchema,
  type IdrSimpleProbabilisticConfig,
} from "@hightouch/lib/idr/create-probabilistic";

const LimitRuleSchema = z.object({
  identifier: z.string(),
  limit: z.number().int().positive(),
});

const MergeRuleSchema = z.object({
  identifier: z.string(),
});

const CreateGraphOperation = z.object({
  action: z.literal("create_graph"),
  name: z.string(),
  description: z.string(),
  connectionId: z.string(),
  outputTable: z
    .string()
    .regex(/^\w+$/, "Only alphanumeric characters and underscores are allowed"),
  schedule: IdrCreateGraphScheduleSchema.optional(),
  probabilisticConfig: IdrSimpleProbabilisticConfigSchema.optional(),
});

const OrderBySchema = z.object({
  order: z.enum(["asc", "desc"]),
  column: z.string(),
});

const AddModelBaseSchema = z.object({
  segmentId: z.string(),
  segmentName: z.string(),
  rank: z.number().int().positive(),
  mappings: z
    .array(
      z.object({
        column: z.string(),
        identifier: z.string(),
      }),
    )
    .min(1),
  goldenRecordBaseSurvivorshipRuleType: z
    .enum(BASE_RECORD_SURVIVORSHIP_TYPES)
    .optional(),
  watermarkLookbackDays: z.number().int().nonnegative().optional(),
  scopeColumn: z.string().min(1).optional(),
});

const AddModelSchema = z.discriminatedUnion("type", [
  AddModelBaseSchema.extend({
    type: z.literal("profile"),
    orderBy: OrderBySchema.optional(),
  }),
  AddModelBaseSchema.extend({
    type: z.literal("event"),
    orderBy: OrderBySchema,
  }),
]);

const AddModelsOperation = z.object({
  action: z.literal("add_models"),
  models: z.array(AddModelSchema).min(1),
});

const SetMergeRulesOperation = z.object({
  action: z.literal("set_merge_rules"),
  mergeRules: z.array(MergeRuleSchema).min(1),
});

const SetLimitRulesOperation = z.object({
  action: z.literal("set_limit_rules"),
  limitRules: z.array(LimitRuleSchema),
});

const CreateGoldenRecordOperation = z.object({
  action: z.literal("create_golden_record"),
});

const GoldenRecordColumnBaseSchema = z.object({
  columnName: z.string(),
});

const GoldenRecordColumnSchema = z.union([
  GoldenRecordColumnBaseSchema.extend({
    survivorshipRuleType: z.enum(SURVIVORSHIP_TYPES),
    identifier: z.string(),
    alias: z.never().optional(),
  }),
  GoldenRecordColumnBaseSchema.extend({
    survivorshipRuleType: z
      .enum(SURVIVORSHIP_TYPES)
      .exclude([SurvivorshipTypes.SOURCE_PRIORITY]),
    alias: z.string(),
    identifier: z.never().optional(),
  }),
]);

const AddGoldenRecordColumnsOperation = z.object({
  action: z.literal("add_golden_record_columns"),
  columns: z.array(GoldenRecordColumnSchema).min(1),
});

const SetColumnSourcePriorityOperation = z.object({
  action: z.literal("set_column_source_priority"),
  columnName: z.string(),
  sourceSurvivorships: z
    .array(
      z.object({
        modelName: z.string(),
        survivorshipRuleType: z.enum(SOURCE_SURVIVORSHIP_TYPES),
      }),
    )
    .min(1),
});

const IdrNonCreateOperationSchema = z.discriminatedUnion("action", [
  AddModelsOperation,
  SetMergeRulesOperation,
  SetLimitRulesOperation,
  CreateGoldenRecordOperation,
  AddGoldenRecordColumnsOperation,
  SetColumnSourcePriorityOperation,
]);

export const IdrOperationSchema = z.union([
  CreateGraphOperation,
  IdrNonCreateOperationSchema,
]);

export type IdrOperation = z.infer<typeof IdrOperationSchema>;

export const IdrChangePlanContentSchema = z.object({
  summary: z.string(),
  graphId: z.null(),
  operations: z.tuple([CreateGraphOperation]).rest(IdrNonCreateOperationSchema),
});

export type IdrChangePlan = z.infer<typeof IdrChangePlanContentSchema>;
